package com.arenaproplay.admin.domain.admin

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/** A club as returned by the backend club endpoints (same shape as the main web app's ClubDto). */
@Serializable
data class ClubRow(
    @SerialName("id") val id: String,
    @SerialName("ownerId") val ownerId: String,
    @SerialName("name") val name: String,
    @SerialName("address") val address: String,
    @SerialName("latitude") val latitude: Double? = null,
    @SerialName("longitude") val longitude: Double? = null,
    @SerialName("type") val type: String,
    @SerialName("customSportName") val customSportName: String? = null,
    @SerialName("mainPhotoUrl") val mainPhotoUrl: String? = null,
    @SerialName("phone") val phone: String? = null,
    @SerialName("description") val description: String? = null,
    @SerialName("instagramUrl") val instagramUrl: String? = null,
    @SerialName("websiteUrl") val websiteUrl: String? = null,
    @SerialName("googleMapsUrl") val googleMapsUrl: String? = null,
    @SerialName("googleSearchUrl") val googleSearchUrl: String? = null,
    @SerialName("averageRating") val averageRating: Double = 0.0,
    @SerialName("totalRatingsCount") val totalRatingsCount: Int = 0,
    @SerialName("isActive") val isActive: Boolean = true,
    @SerialName("gallery") val gallery: List<ClubGalleryImage>? = null,
    @SerialName("rules") val rules: List<ClubRule>? = null,
    @SerialName("createdAt") val createdAt: String,
    @SerialName("updatedAt") val updatedAt: String,
)

@Serializable
data class ClubGalleryImage(
    @SerialName("id") val id: String,
    @SerialName("imageUrl") val imageUrl: String,
    @SerialName("sortOrder") val sortOrder: Int,
)

@Serializable
data class ClubRule(
    @SerialName("id") val id: String,
    @SerialName("text") val text: String,
    @SerialName("sortOrder") val sortOrder: Int,
)

@Serializable
data class ClubService(
    @SerialName("id") val id: String,
    @SerialName("clubId") val clubId: String,
    @SerialName("name") val name: String,
    @SerialName("description") val description: String? = null,
    @SerialName("price") val price: String,
    @SerialName("isEnabled") val isEnabled: Boolean = true,
    @SerialName("createdAt") val createdAt: String,
    @SerialName("updatedAt") val updatedAt: String,
)

@Serializable
data class CreateClubServiceRequest(
    @SerialName("name") val name: String,
    @SerialName("description") val description: String? = null,
    @SerialName("price") val price: String,
)

@Serializable
data class UpdateClubServiceRequest(
    @SerialName("name") val name: String? = null,
    @SerialName("description") val description: String? = null,
    @SerialName("price") val price: String? = null,
    @SerialName("isEnabled") val isEnabled: Boolean? = null,
)

@Serializable
data class ClubListPage(
    @SerialName("clubs") val clubs: List<ClubRow>,
    @SerialName("total") val total: Long,
    @SerialName("page") val page: Int,
    @SerialName("size") val size: Int,
)

@Serializable
data class CreateClubRequest(
    @SerialName("name") val name: String,
    @SerialName("address") val address: String,
    @SerialName("latitude") val latitude: Double? = null,
    @SerialName("longitude") val longitude: Double? = null,
    @SerialName("type") val type: String? = null,
    @SerialName("customSportName") val customSportName: String? = null,
    @SerialName("mainPhotoUrl") val mainPhotoUrl: String? = null,
    @SerialName("phone") val phone: String? = null,
    @SerialName("description") val description: String? = null,
    @SerialName("instagramUrl") val instagramUrl: String? = null,
    @SerialName("websiteUrl") val websiteUrl: String? = null,
    @SerialName("googleMapsUrl") val googleMapsUrl: String? = null,
    @SerialName("googleSearchUrl") val googleSearchUrl: String? = null,
    @SerialName("averageRating") val averageRating: Double? = null,
    @SerialName("totalRatingsCount") val totalRatingsCount: Int? = null,
)

@Serializable
data class UpdateClubRequest(
    @SerialName("name") val name: String? = null,
    @SerialName("address") val address: String? = null,
    @SerialName("latitude") val latitude: Double? = null,
    @SerialName("longitude") val longitude: Double? = null,
    @SerialName("type") val type: String? = null,
    @SerialName("customSportName") val customSportName: String? = null,
    @SerialName("mainPhotoUrl") val mainPhotoUrl: String? = null,
    @SerialName("phone") val phone: String? = null,
    @SerialName("description") val description: String? = null,
    /** Blank string clears the link on the backend; null leaves it unchanged. */
    @SerialName("instagramUrl") val instagramUrl: String? = null,
    @SerialName("websiteUrl") val websiteUrl: String? = null,
    @SerialName("googleMapsUrl") val googleMapsUrl: String? = null,
    @SerialName("googleSearchUrl") val googleSearchUrl: String? = null,
    @SerialName("averageRating") val averageRating: Double? = null,
    @SerialName("totalRatingsCount") val totalRatingsCount: Int? = null,
)

@Serializable
data class WorkingHoursRow(
    @SerialName("id") val id: String,
    @SerialName("dayOfWeek") val dayOfWeek: Int,
    @SerialName("openTime") val openTime: String,
    @SerialName("closeTime") val closeTime: String,
    @SerialName("isClosed") val isClosed: Boolean = false,
)

/** GET /v1/clubs/{id}/schedule — exceptions are ignored, the admin only edits weekly hours. */
@Serializable
data class ClubSchedule(
    @SerialName("workingHours") val workingHours: List<WorkingHoursRow>,
)

@Serializable
data class WorkingHoursEntry(
    @SerialName("dayOfWeek") val dayOfWeek: Int,
    @SerialName("openTime") val openTime: String,
    @SerialName("closeTime") val closeTime: String,
    @SerialName("isClosed") val isClosed: Boolean = false,
)

@Serializable
data class UpdateWorkingHoursRequest(
    @SerialName("entries") val entries: List<WorkingHoursEntry>,
)

@Serializable
data class UpdateClubGalleryRequest(
    @SerialName("images") val images: List<GalleryImageEntry>,
)

@Serializable
data class GalleryImageEntry(
    @SerialName("imageUrl") val imageUrl: String,
)

@Serializable
data class UpdateClubRulesRequest(
    @SerialName("rules") val rules: List<RuleTextEntry>,
)

@Serializable
data class RuleTextEntry(
    @SerialName("text") val text: String,
)

@Serializable
data class UpdateClubAmenitiesRequest(
    @SerialName("amenities") val amenities: List<String>,
)

@Serializable
data class ImageUploadResponse(
    @SerialName("imageUrl") val imageUrl: String,
)

/** One address autocomplete suggestion from the backend Google Places proxy. */
@Serializable
data class AddressSuggestion(
    @SerialName("placeId") val placeId: String,
    @SerialName("title") val title: String,
    @SerialName("subtitle") val subtitle: String? = null,
)

/** Resolved place — coordinates + address parts. */
@Serializable
data class PlaceAddress(
    @SerialName("latitude") val latitude: Double,
    @SerialName("longitude") val longitude: Double,
    @SerialName("street") val street: String? = null,
    @SerialName("houseNumber") val houseNumber: String? = null,
    @SerialName("city") val city: String? = null,
    @SerialName("country") val country: String? = null,
    @SerialName("formattedAddress") val formattedAddress: String? = null,
)

/** Sport reference for the club type dropdown (subset of GET /v1/sports payload). */
@Serializable
data class SportOption(
    @SerialName("id") val id: String,
    @SerialName("name") val name: String,
)
